/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '@/components/auth/AuthProvider'
import {
  FloatingAssistantWidget,
  clampFloatingWidgetPosition,
  type FloatingWidgetPosition,
} from '@/components/voice/FloatingAssistantWidget'
import { PhoneDialerPrompt } from '@/components/voice/PhoneDialerPrompt'
import { VoiceAgentSDK, type ConversationActions } from '@/components/voice/VoiceAgentSDK'
import { CockpitShell } from '@/components/voice/console/CockpitShell'
import { OperationsFloor } from '@/components/voice/console/OperationsFloor'
import { useAblyConsole } from '@/lib/hooks/useAblyConsole'
import { useIsMobile } from '@/lib/hooks/useIsMobile'
import { useLeavePageConfirmation } from '@/lib/hooks/useLeavePageConfirmation'
import { generateSAT } from '@/lib/services/sat-service'
import { useConsoleState, ConsoleStateProvider } from '@/lib/voice-console/state/context'
import { useChatState } from './ChatStateContext'
import {
  buildRouteContextDescription,
  createOpenPhoneDialerTool,
  createNavigateInAppTool,
  isAssistantRoute,
  openNavigationInNewWindow,
  readPhoneDialerLaunchMode,
  type PhoneDialerRequest,
} from '@/lib/voice-console/client-tools'
import { isVoiceSDKAgent, resolveCanonicalAssistantId } from '@/lib/utils/agents'
import { logger } from '@/lib/utils/logger'
import type { ConsoleState, ConnectionMode } from '@/lib/voice-console/state/types'

export type AssistantSurfaceMode = 'hidden' | 'fullscreen' | 'widget'

type BotAssociation = {
  bot_id: string
  name?: string
  description?: string
  meta?: {
    agent_id?: string
    [key: string]: unknown
  }
  [key: string]: unknown
}

type ActiveAssistantRuntime = {
  instanceId: string
  encodedBotId: string
  assistantPath: string
  botAssociation: BotAssociation
}

interface AssistantRuntimeOpenInput {
  encodedBotId: string
  botAssociation: BotAssociation
}

interface AssistantRuntimeContextValue {
  activeRuntime: ActiveAssistantRuntime | null
  activeAssistantId: string | null
  mode: AssistantSurfaceMode
  widgetExpanded: boolean
  isConnected: boolean
  openFullscreen: (input: AssistantRuntimeOpenInput) => void
  minimizeToWidget: () => void
  restoreFullscreen: () => void
  endSession: () => void
  setWidgetExpanded: (expanded: boolean) => void
}

const WIDGET_POSITION_STORAGE_KEY = 'botnexus:assistant-widget-position'

const AssistantRuntimeContext = createContext<AssistantRuntimeContextValue | null>(null)

function readAutoStart(agentId: string): ConnectionMode | 'none' {
  if (!agentId) return 'voice'
  const value = localStorage.getItem(`botnexus:autostart:${agentId}`)
  if (value === 'chat' || value === 'voice' || value === 'none') {
    return value
  }
  return 'voice'
}

function getDefaultWidgetPosition(): FloatingWidgetPosition {
  return {
    x: Math.max(16, window.innerWidth - 456),
    y: Math.max(16, window.innerHeight - 136),
  }
}

function readWidgetPosition(): FloatingWidgetPosition {
  if (typeof window === 'undefined') {
    return { x: 16, y: 16 }
  }

  const fallback = getDefaultWidgetPosition()
  const raw = localStorage.getItem(WIDGET_POSITION_STORAGE_KEY)
  if (!raw) return clampFloatingWidgetPosition(fallback)

  try {
    const parsed = JSON.parse(raw) as Partial<FloatingWidgetPosition>
    if (typeof parsed.x === 'number' && typeof parsed.y === 'number') {
      return clampFloatingWidgetPosition({ x: parsed.x, y: parsed.y })
    }
  } catch (error) {
    logger.warn('[AssistantRuntime] Failed to read widget position', error)
  }

  return clampFloatingWidgetPosition(fallback)
}

function AssistantRuntimeProviderInner({ children }: { children: ReactNode }) {
  const navigate = useNavigate()
  const location = useLocation()
  const isMobile = useIsMobile()
  const { user, userBotData } = useAuth()
  const { setIsChatOpen } = useChatState()
  const { state, dispatch } = useConsoleState()
  const [activeRuntime, setActiveRuntime] = useState<ActiveAssistantRuntime | null>(null)
  const [mode, setMode] = useState<AssistantSurfaceMode>('hidden')
  const [widgetExpanded, setWidgetExpanded] = useState(false)
  const [widgetPosition, setWidgetPosition] = useState<FloatingWidgetPosition>(() => readWidgetPosition())
  const [isSessionActive, setIsSessionActive] = useState(false)
  const [hasUnreadActivity, setHasUnreadActivity] = useState(false)
  const [phoneDialerRequest, setPhoneDialerRequest] = useState<PhoneDialerRequest | null>(null)
  const lastNonAssistantPathRef = useRef('/dashboard/assistants')
  const currentRuntimeIdRef = useRef<string | null>(null)
  const pendingEndSessionRef = useRef(false)
  const autoStartAttemptedRef = useRef(false)
  const actionsRef = useRef<ConversationActions | null>(null)
  const ablyLastProcessedIndexRef = useRef(-1)
  const lastInjectedRouteContextRef = useRef<string | null>(null)
  const activitySnapshotRef = useRef({ messages: 0, tools: 0 })

  const activeVoiceAgentId = activeRuntime?.botAssociation.meta?.agent_id?.trim() ?? null
  const activeAssistantId = resolveCanonicalAssistantId(activeRuntime?.botAssociation)
  const activeBotId = activeRuntime?.botAssociation.bot_id ?? null

  useLeavePageConfirmation(state.isConnected, 'Are you sure you want to leave the call?')

  useEffect(() => {
    if (mode !== 'hidden') {
      setIsChatOpen(true)
      return () => setIsChatOpen(false)
    }

    setIsChatOpen(false)
    return undefined
  }, [mode, setIsChatOpen])

  useEffect(() => {
    if (!isAssistantRoute(location.pathname)) {
      lastNonAssistantPathRef.current = `${location.pathname}${location.search}${location.hash}`
    }
  }, [location.hash, location.pathname, location.search])

  useEffect(() => {
    try {
      localStorage.setItem(WIDGET_POSITION_STORAGE_KEY, JSON.stringify(widgetPosition))
    } catch (error) {
      logger.warn('[AssistantRuntime] Failed to persist widget position', error)
    }
  }, [widgetPosition])

  useEffect(() => {
    if (widgetExpanded || mode !== 'widget') {
      setHasUnreadActivity(false)
      activitySnapshotRef.current = {
        messages: state.messages.length,
        tools: state.activeTools.length,
      }
      return
    }

    const hasNewMessages = state.messages.length > activitySnapshotRef.current.messages
    const hasNewTools = state.activeTools.length > activitySnapshotRef.current.tools
    if (hasNewMessages || hasNewTools) {
      setHasUnreadActivity(true)
    }

    activitySnapshotRef.current = {
      messages: state.messages.length,
      tools: state.activeTools.length,
    }
  }, [mode, state.activeTools.length, state.messages.length, widgetExpanded])

  useEffect(() => {
    if (!user || !userBotData) {
      currentRuntimeIdRef.current = null
      actionsRef.current = null
      pendingEndSessionRef.current = false
      autoStartAttemptedRef.current = false
      ablyLastProcessedIndexRef.current = -1
      lastInjectedRouteContextRef.current = null
      setIsSessionActive(false)
      setWidgetExpanded(false)
      setHasUnreadActivity(false)
      setPhoneDialerRequest(null)
      setMode('hidden')
      setActiveRuntime(null)
      dispatch({ type: 'RESET_CONSOLE' })
    }
  }, [dispatch, user, userBotData])

  useEffect(() => {
    autoStartAttemptedRef.current = false
    actionsRef.current = null
    ablyLastProcessedIndexRef.current = -1
    lastInjectedRouteContextRef.current = null
  }, [activeRuntime?.instanceId])

  const completeEndSession = useCallback(() => {
    currentRuntimeIdRef.current = null
    actionsRef.current = null
    pendingEndSessionRef.current = false
    autoStartAttemptedRef.current = false
    ablyLastProcessedIndexRef.current = -1
    lastInjectedRouteContextRef.current = null
    setHasUnreadActivity(false)
    setIsSessionActive(false)
    setWidgetExpanded(false)
    setPhoneDialerRequest(null)
    setMode('hidden')
    setActiveRuntime(null)
    dispatch({ type: 'RESET_CONSOLE' })

    if (isAssistantRoute(location.pathname)) {
      navigate(lastNonAssistantPathRef.current || '/dashboard/assistants', { replace: true })
    }
  }, [dispatch, location.pathname, navigate])

  const handleRefreshSAT = useCallback(async (): Promise<string | null> => {
    if (!activeAssistantId || !user) return null

    try {
      const sat = await generateSAT(activeAssistantId)
      return sat.token
    } catch (error) {
      logger.warn('[AssistantRuntime] SAT refresh failed (integrations will be unavailable)', error)
      return null
    }
  }, [activeAssistantId, user])

  const keepWidgetOnscreen = useCallback(() => {
    if (isMobile || typeof window === 'undefined') return

    setWidgetPosition((currentPosition) => {
      const nextPosition = clampFloatingWidgetPosition(currentPosition)
      if (nextPosition.x === currentPosition.x && nextPosition.y === currentPosition.y) {
        return currentPosition
      }
      return nextPosition
    })
  }, [isMobile])

  const minimizeRuntime = useCallback((options?: { navigateBack?: boolean }) => {
    if (!activeRuntime) return

    keepWidgetOnscreen()
    setMode('widget')
    setWidgetExpanded(false)
    setHasUnreadActivity(false)

    if (options?.navigateBack !== false && isAssistantRoute(location.pathname)) {
      navigate(lastNonAssistantPathRef.current || '/dashboard/assistants', { replace: true })
    }
  }, [activeRuntime, keepWidgetOnscreen, location.pathname, navigate])

  const restoreFullscreen = useCallback(() => {
    if (!activeRuntime) return

    setMode('fullscreen')
    setWidgetExpanded(false)
    setHasUnreadActivity(false)
    navigate(activeRuntime.assistantPath, { replace: true })
  }, [activeRuntime, navigate])

  const openFullscreen = useCallback((input: AssistantRuntimeOpenInput) => {
    const nextAssistantPath = `/dashboard/assistants/agent/${input.encodedBotId}`
    const sameAssistant = activeBotId === input.botAssociation.bot_id && activeRuntime

    if (sameAssistant) {
      currentRuntimeIdRef.current = activeRuntime.instanceId
      setMode('fullscreen')
      setWidgetExpanded(false)
      setHasUnreadActivity(false)
      setPhoneDialerRequest(null)
      return
    }

    const nextRuntimeId = `${input.botAssociation.bot_id}-${Date.now()}`
    currentRuntimeIdRef.current = nextRuntimeId
    pendingEndSessionRef.current = false
    actionsRef.current = null
    ablyLastProcessedIndexRef.current = -1
    lastInjectedRouteContextRef.current = null
    autoStartAttemptedRef.current = false
    setHasUnreadActivity(false)
    setWidgetExpanded(false)
    setIsSessionActive(false)
    setPhoneDialerRequest(null)
    dispatch({ type: 'RESET_CONSOLE' })
    setActiveRuntime({
      instanceId: nextRuntimeId,
      encodedBotId: input.encodedBotId,
      assistantPath: nextAssistantPath,
      botAssociation: input.botAssociation,
    })
    setMode('fullscreen')
  }, [activeBotId, activeRuntime, dispatch])

  const endSession = useCallback(() => {
    if (!activeRuntime) return

    setHasUnreadActivity(false)
    setWidgetExpanded(false)

    if (state.controls && (state.isConnected || isSessionActive || state.assistantStatus === 'connecting')) {
      pendingEndSessionRef.current = true
      state.controls.disconnect()
      return
    }

    completeEndSession()
  }, [activeRuntime, completeEndSession, isSessionActive, state.assistantStatus, state.controls, state.isConnected])

  const handlePhoneDialerRequested = useCallback((request: PhoneDialerRequest) => {
    setHasUnreadActivity(false)
    setPhoneDialerRequest(request)
  }, [])

  const voiceClientTools = useMemo(() => {
    const navigateInApp = createNavigateInAppTool({
      navigate: (path) => navigate(path),
      onNavigated: () => {
        if (mode === 'fullscreen') {
          minimizeRuntime({ navigateBack: false })
        }
      },
    })

    return {
      navigate_to: openNavigationInNewWindow,
      navigato_to: openNavigationInNewWindow,
      navigate_in_app: navigateInApp,
      open_phone_dialer: createOpenPhoneDialerTool({
        getLaunchMode: readPhoneDialerLaunchMode,
        onDialerRequested: handlePhoneDialerRequested,
      }),
    }
  }, [handlePhoneDialerRequested, minimizeRuntime, mode, navigate])

  const handleConnecting = useCallback(() => {
    const runtimeId = activeRuntime?.instanceId
    if (!runtimeId || currentRuntimeIdRef.current !== runtimeId) return

    pendingEndSessionRef.current = false
    setIsSessionActive(true)
    dispatch({ type: 'ERROR_CLEARED' })
    dispatch({ type: 'STATUS_CHANGED', status: 'connecting' })
  }, [activeRuntime?.instanceId, dispatch])

  const handleSdkConnect = useCallback(() => {
    const runtimeId = activeRuntime?.instanceId
    if (!runtimeId || currentRuntimeIdRef.current !== runtimeId || !activeRuntime || !userBotData?.user_id) {
      return
    }

    // Reassert this on the confirmed SDK connection. During replacement-session
    // transitions, the outgoing session's delayed disconnect can arrive after
    // the new attempt has already marked itself active.
    setIsSessionActive(true)
    dispatch({ type: 'ELEVENLABS_CONNECTED' })
    dispatch({
      type: 'SESSION_STARTED',
        session: {
          sessionId: `session-${Date.now()}`,
          agentId: resolveCanonicalAssistantId(activeRuntime.botAssociation) ?? '',
          userId: userBotData.user_id,
          agentName: activeRuntime.botAssociation.name ?? 'Assistant',
          mode: 'assistant',
        startedAt: Date.now(),
      },
    })
  }, [activeRuntime, dispatch, userBotData?.user_id])

  const handleSdkDisconnect = useCallback((intent: 'manual' | 'mode-switch' | 'unknown') => {
    const runtimeId = activeRuntime?.instanceId
    if (!runtimeId || currentRuntimeIdRef.current !== runtimeId) return

    actionsRef.current = null
    setIsSessionActive(false)
    if (intent !== 'mode-switch') {
      lastInjectedRouteContextRef.current = null
    }
    dispatch({ type: 'ELEVENLABS_DISCONNECTED' })
    dispatch({ type: 'SESSION_ENDED' })

    if (pendingEndSessionRef.current) {
      completeEndSession()
    }
  }, [activeRuntime?.instanceId, completeEndSession, dispatch])

  const handleSdkModeChange = useCallback((modeValue: string) => {
    const runtimeId = activeRuntime?.instanceId
    if (!runtimeId || currentRuntimeIdRef.current !== runtimeId) return

    const statusMap = {
      listening: 'listening',
      speaking: 'speaking',
      thinking: 'processing',
      processing: 'processing',
    } as const

    dispatch({ type: 'STATUS_CHANGED', status: statusMap[modeValue as keyof typeof statusMap] ?? 'idle' })
  }, [activeRuntime?.instanceId, dispatch])

  const handleSdkMessage = useCallback((message: { role: 'user' | 'agent'; text: string; timestamp: number }) => {
    const runtimeId = activeRuntime?.instanceId
    if (!runtimeId || currentRuntimeIdRef.current !== runtimeId) return

    dispatch({
      type: 'MESSAGE_ADDED',
      message: {
        id: `msg-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        role: message.role,
        text: message.text,
        timestamp: message.timestamp,
      },
    })
  }, [activeRuntime?.instanceId, dispatch])

  const handleControlsReady = useCallback((controls: NonNullable<ConsoleState['controls']>) => {
    const runtimeId = activeRuntime?.instanceId
    if (!runtimeId || currentRuntimeIdRef.current !== runtimeId) return

    dispatch({ type: 'SET_CONTROLS', controls })

    if (!autoStartAttemptedRef.current && activeAssistantId) {
      autoStartAttemptedRef.current = true
      const preference = readAutoStart(activeAssistantId)
      if (preference !== 'none') {
        controls.connect(preference)
      }
    }
  }, [activeAssistantId, activeRuntime?.instanceId, dispatch])

  const handleConnectionModeChange = useCallback((nextMode: ConnectionMode | null) => {
    const runtimeId = activeRuntime?.instanceId
    if (!runtimeId || currentRuntimeIdRef.current !== runtimeId) return

    dispatch({ type: 'SET_CONNECTION_MODE', mode: nextMode })
    dispatch({ type: 'INPUT_MODE_CHANGED', inputMode: nextMode === 'chat' ? 'text' : 'voice' })
    if (nextMode) {
      dispatch({ type: 'ELEVENLABS_CONNECTED' })
    }
  }, [activeRuntime?.instanceId, dispatch])

  const handleMicMutedChange = useCallback((muted: boolean) => {
    const runtimeId = activeRuntime?.instanceId
    if (!runtimeId || currentRuntimeIdRef.current !== runtimeId) return
    dispatch({ type: 'SET_MIC_MUTED', muted })
  }, [activeRuntime?.instanceId, dispatch])

  const handleAgentModeChange = useCallback((agentMode: 'default' | 'meeting' | 'translator') => {
    const runtimeId = activeRuntime?.instanceId
    if (!runtimeId || currentRuntimeIdRef.current !== runtimeId) return
    dispatch({ type: 'SET_AGENT_MODE', agentMode })
  }, [activeRuntime?.instanceId, dispatch])

  const handleActionsReady = useCallback((actions: ConversationActions) => {
    const runtimeId = activeRuntime?.instanceId
    if (!runtimeId || currentRuntimeIdRef.current !== runtimeId) return
    actionsRef.current = actions
  }, [activeRuntime?.instanceId])

  const { events: ablyEvents, connectionState: ablyConnectionState } = useAblyConsole({
    agentId: activeAssistantId ?? '',
    userId: userBotData?.user_id ?? '',
    enabled: !!activeAssistantId && !!userBotData?.user_id && isSessionActive && !!activeRuntime,
  })

  useEffect(() => {
    dispatch({ type: 'ABLY_CONNECTION_STATE_CHANGED', connectionState: ablyConnectionState })
  }, [ablyConnectionState, dispatch])

  useEffect(() => {
    const newEvents = ablyEvents.slice(ablyLastProcessedIndexRef.current + 1)
    if (newEvents.length === 0) return
    ablyLastProcessedIndexRef.current = ablyEvents.length - 1

    const readString = (...values: unknown[]): string | undefined => {
      for (const value of values) {
        if (typeof value === 'string' && value.trim()) return value
      }
      return undefined
    }

    for (const ablyEvent of newEvents) {
      const { type, data, id, timestamp } = ablyEvent
      const tool = (typeof data.tool === 'string' && data.tool) || 'unknown'
      const invocationId = readString(
        data.invocation_id,
        data.tool_invocation_id,
        data.tool_run_id,
        data.run_id,
        data.request_id
      )
      const operation = readString(data.operation)
      const sessionId = readString(data.session_id, data.voice_session_id, data.call_session_id)
      const step = typeof data.step === 'number' ? data.step : 1
      const ofSteps = typeof data.of_steps === 'number' ? data.of_steps : null
      const stepLabel = typeof data.step_label === 'string' ? data.step_label : undefined

      if (type === 'tool:started') {
        dispatch({
          type: 'TOOL_STARTED',
          id,
          tool,
          invocation_id: invocationId,
          operation,
          session_id: sessionId,
          step,
          of_steps: ofSteps,
          step_label: stepLabel,
          timestamp,
        })
        continue
      }

      if (type === 'tool:progress') {
        dispatch({
          type: 'TOOL_PROGRESS',
          id,
          tool,
          invocation_id: invocationId,
          operation,
          session_id: sessionId,
          step,
          of_steps: ofSteps,
          step_label: stepLabel,
        })
        continue
      }

      if (type === 'tool:completed') {
        dispatch({
          type: 'TOOL_COMPLETED',
          id,
          tool,
          invocation_id: invocationId,
          operation,
          session_id: sessionId,
          timestamp,
          step,
          of_steps: ofSteps,
        })
        continue
      }

      if (type === 'tool:failed') {
        const rawError = data.error
        const errorText =
          typeof rawError === 'string'
            ? rawError
            : rawError != null &&
                typeof rawError === 'object' &&
                typeof (rawError as Record<string, unknown>).message === 'string'
              ? ((rawError as Record<string, unknown>).message as string)
              : 'Tool failed'

        logger.warn('[AssistantRuntime] Tool failed payload', { tool, data })
        dispatch({
          type: 'TOOL_FAILED',
          id,
          tool,
          invocation_id: invocationId,
          operation,
          session_id: sessionId,
          error: errorText,
          timestamp,
        })
      }
    }
  }, [ablyEvents, dispatch])

  useEffect(() => {
    if (!activeRuntime || !state.isConnected || !actionsRef.current) return

    const currentDescription = buildRouteContextDescription(location.pathname)
    if (lastInjectedRouteContextRef.current === currentDescription) return

    actionsRef.current.sendContextualUpdate(currentDescription)
    lastInjectedRouteContextRef.current = currentDescription
  }, [activeRuntime, location.pathname, state.isConnected])

  const runtimeValue = useMemo<AssistantRuntimeContextValue>(() => ({
    activeRuntime,
    activeAssistantId,
    mode,
    widgetExpanded,
    isConnected: state.isConnected,
    openFullscreen,
    minimizeToWidget: () => minimizeRuntime(),
    restoreFullscreen,
    endSession,
    setWidgetExpanded: (expanded: boolean) => {
      setWidgetExpanded(expanded)
      if (expanded) {
        setHasUnreadActivity(false)
      }
    },
  }), [
    activeAssistantId,
    activeRuntime,
    endSession,
    minimizeRuntime,
    mode,
    openFullscreen,
    restoreFullscreen,
    state.isConnected,
    widgetExpanded,
  ])

  const shouldRenderRuntime = !!activeRuntime && !!user && !!userBotData && isVoiceSDKAgent(activeRuntime.botAssociation.meta)
  const fullscreenStyle = isMobile
    ? {
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'env(safe-area-inset-bottom)',
        paddingLeft: 'env(safe-area-inset-left)',
        paddingRight: 'env(safe-area-inset-right)',
      }
    : undefined

  const handleWidgetVoiceAction = useCallback(() => {
    const controls = state.controls
    if (!controls) return

    if (!state.isConnected) {
      controls.connect('voice')
      return
    }

    if (state.connectionMode === 'voice') {
      controls.toggleMic()
      return
    }

    controls.switchMode('voice')
  }, [state.connectionMode, state.controls, state.isConnected])

  const handleWidgetTextAction = useCallback(() => {
    const controls = state.controls
    if (!controls) return

    setHasUnreadActivity(false)

    if (!state.isConnected) {
      controls.connect('chat')
      return
    }

    if (state.connectionMode !== 'chat') {
      controls.switchMode('chat')
    }
  }, [state.connectionMode, state.controls, state.isConnected])

  return (
    <AssistantRuntimeContext.Provider value={runtimeValue}>
      {children}
      {shouldRenderRuntime && mode !== 'hidden' && activeRuntime && (
        <FloatingAssistantWidget
          agentName={activeRuntime.botAssociation.name ?? 'Assistant'}
          assistantStatus={state.assistantStatus}
          unreadActivity={hasUnreadActivity}
          expanded={widgetExpanded}
          isMobile={isMobile}
          isConnected={state.isConnected}
          connectionMode={state.connectionMode}
          isMicMuted={state.isMicMuted}
          position={widgetPosition}
          surfaceMode={mode}
          fullscreenStyle={fullscreenStyle}
          getInputVolume={state.controls?.getInputVolume}
          getOutputFrequencyData={state.controls?.getOutputFrequencyData}
          onPositionChange={setWidgetPosition}
          onExpandChange={(expanded) => {
            setWidgetExpanded(expanded)
            if (expanded) {
              setHasUnreadActivity(false)
            }
          }}
          onRestoreFullscreen={restoreFullscreen}
          onVoiceAction={handleWidgetVoiceAction}
          onTextAction={handleWidgetTextAction}
        >
          <CockpitShell
            agentName={activeRuntime.botAssociation.name}
            agentId={activeAssistantId ?? undefined}
            className="h-full min-h-0"
          >
            <VoiceAgentSDK
              key={activeRuntime.instanceId}
              agentId={activeVoiceAgentId ?? ''}
              agentName={activeRuntime.botAssociation.name}
              initialAutoConnect={false}
              forceMobileMode={mode === 'fullscreen'}
              userId={userBotData.user_id}
              authUserId={user.id}
              botId={activeRuntime.botAssociation.bot_id}
              role={userBotData.role}
              fullName={user.user_metadata?.full_name}
              email={user.email}
              provider={user.user_metadata?.provider_id}
              issuer={user.user_metadata?.iss}
              providerId={user.user_metadata?.provider_id}
              phone={user.phone_confirmed_at ? user.phone : undefined}
              onRefreshSAT={handleRefreshSAT}
              clientTools={voiceClientTools}
              onActionsReady={handleActionsReady}
              onConnecting={handleConnecting}
              onSdkConnect={handleSdkConnect}
              onSdkDisconnect={handleSdkDisconnect}
              onSdkModeChange={handleSdkModeChange}
              onSdkMessage={handleSdkMessage}
              onControlsReady={handleControlsReady}
              onConnectionModeChange={handleConnectionModeChange}
              onMicMutedChange={handleMicMutedChange}
              onAgentModeChange={handleAgentModeChange}
              topSlot={<OperationsFloor />}
            />
          </CockpitShell>
        </FloatingAssistantWidget>
      )}
      <PhoneDialerPrompt request={phoneDialerRequest} onDismiss={() => setPhoneDialerRequest(null)} />
    </AssistantRuntimeContext.Provider>
  )
}

export function AssistantRuntimeProvider({ children }: { children: ReactNode }) {
  return (
    <ConsoleStateProvider>
      <AssistantRuntimeProviderInner>{children}</AssistantRuntimeProviderInner>
    </ConsoleStateProvider>
  )
}

export function useAssistantRuntime(): AssistantRuntimeContextValue {
  const context = useContext(AssistantRuntimeContext)
  if (!context) {
    throw new Error('useAssistantRuntime must be used within AssistantRuntimeProvider')
  }
  return context
}
